import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// ─── Invoices API ─────────────────────────────────────────────────────
// GET /v1/invoices — paginated list of the client's invoices (one row per
// scanned surat-jalan / proof document, keyed by airwaybill). Same LINK channel
// + live-mode headers as the other client endpoints; the bearer token is
// attached by the shared axios interceptor.
const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

// Aging tiers returned by the gateway (drives the aging pill colour). Kept loose
// — the pill falls back to a neutral style for anything unmapped.
export const INVOICE_AGING_TIERS = ['green', 'amber', 'red'] as const;
export type InvoiceAgingTier = (typeof INVOICE_AGING_TIERS)[number];

// Status groups shared by the list tabs and the export modal. Single source of
// truth so an export's `status` param always matches what the list tab shows —
// IN_PIPELINE fans out across the four pre-confirmation statuses. Consumers
// supply their own UI labels (the tab and export use different copy).
export const INVOICE_STATUS_GROUPS = {
  IN_PIPELINE: [
    'PENDING_COLLECTION',
    'RECEIVED_AT_HUB',
    'RECEIVED_BY_RIDER',
    'EXCEPTION',
  ],
  WAITING_CONFIRMATION: ['WAITING_CONFIRMATION'],
  DISPUTED: ['DISPUTED'],
  RESOLVED: ['RESOLVED'],
} as const;

export type InvoiceStatusGroup = keyof typeof INVOICE_STATUS_GROUPS;

// The comma-separated `status` query value for a group.
export const invoiceStatusGroupParam = (g: InvoiceStatusGroup): string =>
  INVOICE_STATUS_GROUPS[g].join(',');

// A nested actor (rider / client) on an invoice row.
export interface InvoiceParty {
  id: string;
  name: string;
}

// outlet / hub on the detail payload — a named location (outlet also carries an
// address). On the list rows these come through as null.
export interface InvoiceLocation {
  id?: string;
  name?: string;
  address?: string;
}

// A scanned document (surat jalan / faktur) attached to an invoice or a
// status-change event.
export interface InvoiceImage {
  imageId?: string;
  imageUrl: string;
  documentType?: string;
}

export interface InvoiceImages {
  images: InvoiceImage[];
  spType?: string;
}

// A single row in the invoices list response.
export interface Invoice {
  id: string;
  invoiceNumber: string;
  airwaybill: string;
  batchId?: string | null;
  rider?: InvoiceParty | null;
  client?: InvoiceParty | null;
  outlet?: InvoiceLocation | string | null;
  hub?: InvoiceLocation | string | null;
  itemsCount?: number;
  issueDate?: string | null; // YYYY-MM-DD
  dueDate?: string | null; // YYYY-MM-DD
  status: string;
  invoiceImages?: InvoiceImages | null;
  returnedAt?: string | null;
  exceptionCode?: string | null;
  notes?: string | null;
  daysSince?: number | null;
  agingTier?: InvoiceAgingTier | string | null;
  createdAt?: string;
  updatedAt?: string;
}

// The envelope is { status, data: { items, page, pageSize, total } }.
export interface InvoiceListResponse {
  status: string;
  data: {
    items: Invoice[];
    page: number;
    pageSize: number;
    total: number;
  };
}

export interface InvoicePagination {
  total: number;
  page: number;
  pageSize: number;
}

export interface InvoiceListParams {
  page?: number;
  pageSize?: number;
  clientId?: string | number;
  search?: string; // invoice #, airwaybill, rider, outlet
  status?: string; // comma-separated status values
  agingTier?: string; // comma-separated aging tiers
  issuedFrom?: string; // YYYY-MM-DD
  issuedTo?: string; // YYYY-MM-DD
}

export const getInvoices = async (
  params: InvoiceListParams = {}
): Promise<{ data: Invoice[]; pagination: InvoicePagination }> => {
  try {
    const query: Record<string, string | number> = {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.clientId) query.clientId = params.clientId;
    if (params.search) query.search = params.search;
    if (params.status) query.status = params.status;
    if (params.agingTier) query.agingTier = params.agingTier;
    if (params.issuedFrom) query.issuedFrom = params.issuedFrom;
    if (params.issuedTo) query.issuedTo = params.issuedTo;

    const response = await Axios.get<InvoiceListResponse>('/v1/invoices', {
      headers: CHANNEL_HEADERS_LIVE,
      params: query,
    });
    return {
      data: response.data.data?.items ?? [],
      pagination: {
        total: response.data.data?.total ?? 0,
        page: response.data.data?.page ?? params.page ?? 1,
        pageSize: response.data.data?.pageSize ?? params.pageSize ?? 20,
      },
    };
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─── Detail ───────────────────────────────────────────────────────────
// GET /v1/invoices/{id}/detail — the full invoice plus its status-change
// history (and a `related` block the gateway hasn't populated yet).
export interface InvoiceHistoryActor {
  id?: string;
  name?: string;
  role?: string;
}

export interface InvoiceHistoryEntry {
  id: string;
  fromStatus?: string | null;
  toStatus: string;
  changedAt: string;
  changedBy?: InvoiceHistoryActor | null;
  note?: string | null;
  invoiceImages?: InvoiceImages | null;
}

export interface InvoiceDetail {
  invoice: Invoice;
  history: InvoiceHistoryEntry[];
  related?: unknown;
}

export interface InvoiceDetailResponse {
  status: string;
  data: InvoiceDetail;
}

export const getInvoiceDetail = async (id: string): Promise<InvoiceDetail> => {
  try {
    const response = await Axios.get<InvoiceDetailResponse>(
      `/v1/invoices/${id}/detail`,
      { headers: CHANNEL_HEADERS_LIVE }
    );
    const data = response.data.data;
    return {
      invoice: data.invoice,
      history: data.history ?? [],
      related: data.related ?? null,
    };
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─── Summary ──────────────────────────────────────────────────────────
// GET /v1/invoices/summary — aggregate counts used by the summary cards.
export interface InvoiceSummary {
  totalToday?: number;
  receivedByRider?: number;
  received?: number;
  pending?: number;
  exception?: number;
  redAlerts?: number;
  awaitingConfirmation?: number;
  disputed?: number;
  resolved?: number;
}

interface InvoiceSummaryResponse {
  status: string;
  data: InvoiceSummary;
}

export const getInvoiceSummary = async (
  params: {
    clientId?: string | number;
  } = {}
): Promise<InvoiceSummary> => {
  try {
    const query: Record<string, string | number> = {};
    if (params.clientId) query.clientId = params.clientId;
    const response = await Axios.get<InvoiceSummaryResponse>(
      '/v1/invoices/summary',
      { headers: CHANNEL_HEADERS_LIVE, params: query }
    );
    return response.data.data ?? {};
  } catch {
    // Summary is non-critical — leave cards at 0 if it fails.
    return {};
  }
};

// ─── Export ───────────────────────────────────────────────────────────
// GET /v1/invoices/export — streams a CSV/XLSX file of the matching invoices,
// scoped to an explicit `issueDateFrom`/`issueDateTo` range.
export type InvoiceExportFormat = 'csv' | 'xlsx';

export interface InvoiceExportParams {
  clientId?: string | number;
  status?: string; // comma-separated status values
  issueDateFrom?: string; // YYYY-MM-DD
  issueDateTo?: string; // YYYY-MM-DD
  format: InvoiceExportFormat;
}

/**
 * Decodes an error body that came back as a Blob.
 *
 * The export request sets `responseType: 'blob'`, so axios hands back a 4xx
 * body as a Blob too — `error.response.data.error` is then `undefined` and the
 * generic handler falls through to axios' own "Request failed with status code
 * 400". This reads the blob and pulls the backend's message out, e.g.
 * `{ status: 'Failed', error: 'No received invoices in this range.',
 *    code: 'EXPORT_EMPTY' }` → "No received invoices in this range."
 *
 * Scoped to the invoice export on purpose: backend error shapes aren't
 * standardised yet, so this doesn't try to be the app-wide handler. Returns
 * `null` when the body isn't JSON or carries no readable message, so the
 * caller can fall back to `getBackendErrorMessage`.
 */
const readBlobErrorMessage = async (error: any): Promise<string | null> => {
  const data = error?.response?.data;
  if (!(data instanceof Blob)) return null;

  try {
    const parsed = JSON.parse(await data.text());
    const message = parsed?.error ?? parsed?.message;
    return typeof message === 'string' && message.trim() !== ''
      ? message
      : null;
  } catch {
    return null;
  }
};

export const exportInvoices = async (
  params: InvoiceExportParams
): Promise<void> => {
  try {
    const query: Record<string, string | number> = { format: params.format };
    if (params.clientId) query.clientId = params.clientId;
    if (params.status) query.status = params.status;
    if (params.issueDateFrom) query.issueDateFrom = params.issueDateFrom;
    if (params.issueDateTo) query.issueDateTo = params.issueDateTo;

    const response = await Axios.get('/v1/invoices/export', {
      headers: CHANNEL_HEADERS_LIVE,
      params: query,
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type:
        params.format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.${params.format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    const blobMessage = await readBlobErrorMessage(error);
    throw new Error(blobMessage ?? getBackendErrorMessage(error));
  }
};

// ─── Resolve / Dispute ────────────────────────────────────────────────
// Awaiting-confirmation actions — only a WAITING_CONFIRMATION invoice can be
// resolved or disputed. The LINK (provider) caller must forward `updatedBy`
// (the acting user's email) — the provider JWT carries no name of its own.
//
// Dispute reasons mirror the gateway's InvoiceDisputeReason enum; labels are
// the client-facing Indonesian copy from the prototype.
export const INVOICE_DISPUTE_REASONS = [
  { value: 'MISSING_STAMP', label: 'Surat jalan tidak ada stamp' },
  { value: 'MISSING_SIGNATURE', label: 'Surat jalan tidak ada tanda tangan' },
  { value: 'WRONG_OUTLET_NAME', label: 'Salah nama outlet' },
  { value: 'WRONG_ITEMS', label: 'Barang tidak sesuai' },
  { value: 'ILLEGIBLE', label: 'Surat jalan tidak terbaca' },
  { value: 'DAMAGED', label: 'Barang rusak' },
  { value: 'OTHER', label: 'Lainnya' },
] as const;

export type InvoiceDisputeReason =
  (typeof INVOICE_DISPUTE_REASONS)[number]['value'];

// POST /v1/invoices/{id}/resolve — note optional.
export const resolveInvoice = async (
  id: string,
  body: { note?: string | null; updatedBy: string }
): Promise<void> => {
  try {
    await Axios.post(`/v1/invoices/${id}/resolve`, body, {
      headers: CHANNEL_HEADERS_LIVE,
    });
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// POST /v1/invoices/{id}/dispute — reason + note both required.
export const disputeInvoice = async (
  id: string,
  body: { reason: InvoiceDisputeReason; note: string; updatedBy: string }
): Promise<void> => {
  try {
    await Axios.post(`/v1/invoices/${id}/dispute`, body, {
      headers: CHANNEL_HEADERS_LIVE,
    });
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};
